import clsx from 'clsx';
import React from 'react';

import { ConditionalWrapper } from '../ConditionalWrapper';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { QuoteForm } from '../QuoteForm';

import type { ButtonProps } from '../Button/Button.types';
import type { CTAProps } from '../CTA/CTA.types';
import type { BoomboxProps } from './Boombox.types';

export const Boombox = ({
  mainHeadingText,
  mainLeadText,
  quoteForm,
  ctas,
}: BoomboxProps): JSX.Element => {
  function isButton(_ctas: ButtonProps | CTAProps[]): _ctas is ButtonProps {
    return ctas && ('onClick' in _ctas || 'href' in _ctas) ? true : false;
  }

  function isCTAArray(_ctas: ButtonProps | CTAProps[]): _ctas is CTAProps[] {
    return ctas && 'map' in _ctas ? true : false;
  }

  // Banner Boombox
  if (isButton(ctas)) {
    return (
      <div className='boombox-banner'>
        <div className='boombox-banner__container'>
          {mainHeadingText && (
            <div className='boombox-banner__title'>{mainHeadingText}</div>
          )}
          <div className='boombox-banner__content'>
            {mainLeadText && (
              <div className='boombox-banner__summary'>{mainLeadText}</div>
            )}
            <Button {...ctas} />
          </div>
        </div>
      </div>
    );
  }

  // Standard Boombox
  return (
    <div
      className={clsx('boombox', {
        ['boombox_horizontal']: isCTAArray(ctas) && ctas.length === 1,
      })}
    >
      <div className='boombox__wrapper'>
        <div className='boombox__card_primary'>
          {/* Main Header Text */}
          <div className='boombox__card_heading text-heading_2'>
            {mainHeadingText}
          </div>

          {/* Main Lead Text */}
          <div className='boombox__card_callout text-lead_2'>
            {mainLeadText}
          </div>
        </div>

        <ConditionalWrapper
          condition={isCTAArray(ctas) && ctas.length > 1}
          wrapper={(children: React.ReactNode) => (
            <div className='boombox__cta-wrapper'>{children}</div>
          )}
        >
          <>
            {ctas &&
              isCTAArray(ctas) &&
              ctas.map(({ headingText, leadText, buttons, icon }, index) => (
                <div key={index} className='boombox__card_secondary'>
                  {/* Single Panel Icon Wrapper */}
                  {icon && (
                    <ConditionalWrapper
                      condition={ctas.length === 1}
                      wrapper={(children: React.ReactNode) => (
                        <div className='boombox__icon-wrapper'>{children}</div>
                      )}
                    >
                      <div className='boombox__card_icon'>
                        <Icon {...icon} />
                      </div>
                    </ConditionalWrapper>
                  )}

                  {/* Single Panel Wrapper */}
                  <ConditionalWrapper
                    condition={ctas.length === 1}
                    wrapper={(children: React.ReactNode) => (
                      <div className='boombox__card_subheading-wrapper'>
                        {children}
                      </div>
                    )}
                  >
                    <>
                      {/* Main Text */}
                      {headingText && (
                        <div
                          className={clsx(
                            'boombox__card_subheading',
                            'text-heading_3'
                          )}
                        >
                          {headingText}
                        </div>
                      )}

                      {/* Lead Text */}
                      {leadText && (
                        <div
                          className={clsx(
                            'boombox__card_description',
                            'text_3'
                          )}
                        >
                          {leadText}
                        </div>
                      )}

                      {/* Quote Form */}
                      {index === 0 && quoteForm && <QuoteForm {...quoteForm} />}

                      {/* CTA Button */}
                      {buttons && buttons[0] && (
                        <div className='boombox__button-wrapper'>
                          <Button {...buttons[0]} />
                        </div>
                      )}
                    </>
                  </ConditionalWrapper>
                </div>
              ))}
          </>
        </ConditionalWrapper>
      </div>
    </div>
  );
};
